import { useEffect, useId, useMemo, useState } from "react";
import { Bell, CheckCircle2, Clock3, FileText, MessageCircle } from "lucide-react";
import { create } from "zustand";
import { Badge, Button, Dialog, DialogActions, Field, FilterBar, IconButton, Select } from "@studio";
import { KeywordInput } from "@studio-features/meta-crew/seo-shared";
import { can, useMe } from "../lib/access";
import { api, errorMessage } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { keys, useAction, useJobComments, useJobRevisions, useRun, useRuns, useTask, type JobRevision } from "../lib/queries";
import type { RunDetail, RunRow, TaskDetail } from "../lib/types";
import { InfoRow, Loading, Panel, PanelError } from "../console/ui";
import { ManuscriptEditor, useManuscriptPreview, VISIBILITY_NAMES } from "./create-task";
import { jobTitle } from "./runs";

/**
 * 고객 발행 (시안 951f2f6 customer-publish.tsx: PublishList · PublishDetail · PublishComposer · NewPublishDialog ·
 * CustomerPublishNotifications). 시안은 메모리 스토어(usePublish)를 쓰므로 같은 마크업(lwpub-*)을 실제 API 에 연결해 콘솔에 둔다.
 * 요청 = Job(origin=customer) + Run. 고객용 상태 9개는 Run·Task 상태에서 계산한다.
 */

export type PublishStatus = "확인 중" | "수정 요청" | "발행 대기" | "발행 중" | "발행됨" | "발행 실패" | "취소됨" | "반려됨" | "삭제됨";
export const PUBLISH_STATUSES: PublishStatus[] = ["확인 중", "수정 요청", "발행 대기", "발행 중", "발행됨", "발행 실패", "취소됨", "반려됨", "삭제됨"];
export const PUBLISH_STAGES = ["원고 검토", "작업 수행", "작업 완료", "완료 검토", "최종 보고"];
/** 고객이 다시 시도할 수 있는 환경 실패 (서버 retry 와 같은 목록). */
const ENV_FAILURES = new Set(["device_unavailable", "worker_lost", "needs_human", "login_required", "llm_limit", "pre_step_failed"]);
const STAGE_INDEX: Record<string, number> = { review: 0, run: 1, posted: 2, verify: 3, done: 5 };

type PublishRunLike = Pick<RunRow, "status" | "errorKind" | "stage" | "jobRunAt"> & Partial<Pick<RunRow, "taskStatus" | "reviewStatus" | "taskErrorKind">>;

export function publishStatusOf(r: PublishRunLike, taskStatus?: string | null): PublishStatus {
  if (r.reviewStatus === "deleted") return "삭제됨";
  if (r.status === "held") {
    if (r.errorKind === "approval_required") return "확인 중";
    if (r.errorKind === "changes_requested") return "수정 요청";
    return "발행 대기";
  }
  const ts = taskStatus ?? r.taskStatus;
  if (r.status === "succeeded") return "발행됨";
  if (r.status === "failed" || r.status === "dead") return "발행 실패";
  if (r.status === "cancelled") return r.errorKind === "rejected" ? "반려됨" : "취소됨";
  if (r.status === "running" || ts === "running" || ts === "assigned") return "발행 중";
  return "발행 대기";
}
const publishTone = (s: PublishStatus) => (["수정 요청", "발행 실패", "반려됨"].includes(s) ? "amber" : s === "발행됨" ? "teal" : s === "발행 중" ? "blue" : "neutral");
const stageIndexOf = (status: PublishStatus, stage?: string | null) => (status === "발행됨" ? 5 : stage && STAGE_INDEX[stage] !== undefined ? STAGE_INDEX[stage]! : 0);
const futureAt = (s?: string | null) => (s && new Date(s).getTime() > Date.now() ? s : null);
/** 직원 화면에서 쓰는 고객용 실패 문구 (고객 API 는 서버가 이미 바꿔 준다). */
function failureText(kind?: string | null, fallback?: string | null): string {
  if (kind === "needs_human" || kind === "login_required") return "휴대폰에서 로그인·인증 확인이 필요합니다";
  if (kind === "device_unavailable" || kind === "worker_lost" || kind === "pre_step_failed") return "현장 PC·휴대폰 연결을 확인해 주세요";
  if (kind === "customer_inactive") return "서비스 상태로 발행이 중단되었습니다";
  if (kind === "llm_limit") return "AI 사용 한도로 잠시 미뤄졌습니다";
  return fallback ?? "발행 중 문제가 생겨 운영팀이 확인하고 있습니다";
}

/* ---------- 열린 요청 (헤더 알림·목록 → 상세) ---------- */

export const usePublishFocus = create<{ runId: string | null; open: (id: string | null) => void }>((set) => ({ runId: null, open: (runId) => set({ runId }) }));

/* ---------- 진행 단계 ---------- */

export function PublishProgress({ current, compact = false }: { current: number; compact?: boolean }) {
  return (
    <ol className={`lwpub-progress ${compact ? "is-compact" : ""}`} aria-label="발행 진행 단계">
      {PUBLISH_STAGES.map((stage, i) => (
        <li key={stage} className={i < current ? "is-done" : i === current ? "is-current" : ""} aria-current={i === current ? "step" : undefined}>
          <span>{i < current ? <CheckCircle2 size={14} /> : i + 1}</span>
          <small>{stage}</small>
        </li>
      ))}
    </ol>
  );
}

/* ---------- 목록 ---------- */

export function PublishList({
  customerId,
  staff = false,
  canWrite = false,
  approval = "staff",
  onCreate,
  onOpen,
  pendingOnly = false,
}: {
  customerId: string;
  staff?: boolean;
  canWrite?: boolean;
  approval?: "staff" | "auto";
  onCreate?: () => void;
  onOpen: (runId: string) => void;
  pendingOnly?: boolean;
}) {
  const q = useRuns(customerId, { origin: "customer", limit: 100 });
  const [filter, setFilter] = useState<string>(pendingOnly ? "확인 중" : "전체");
  // 요청서 35-7: 고객 발행 = 원고 소유가 이 고객사인 작업 (서버도 같은 범위로 거른다). 소유 정보가 없는 옛 행은 origin=customer
  const own = useMemo(() => (q.data?.items ?? []).filter((r) => r.kind === "machine_task" && (r.ownerCustomerId === undefined ? r.origin === "customer" : r.ownerCustomerId === customerId)), [q.data, customerId]);
  const rows = own.map((r) => ({ r, status: publishStatusOf(r) }));
  const visible = rows.filter((x) => filter === "전체" || x.status === filter);
  return (
    <div className="lwd-stack" aria-label={staff ? "고객 발행 요청 목록" : "내 발행 작업 목록"}>
      {!staff && (
        <div className="lwpub-policy">
          <Clock3 size={18} />
          <span>{approval === "auto" ? "형식에 맞는 원고는 접수 즉시 발행됩니다." : "운영팀 확인 뒤 발행됩니다."}</span>
        </div>
      )}
      <FilterBar>
        <Select label="발행 상태" value={filter} onChange={(e) => setFilter(e.target.value)}>
          {["전체", ...PUBLISH_STATUSES].map((v) => (
            <option key={v}>{v}</option>
          ))}
        </Select>
        <span className="meta-crew-muted">{visible.length}건</span>
      </FilterBar>
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <PanelError error={errorMessage(q.error)} onRetry={() => void q.refetch()} />
      ) : !visible.length ? (
        <div className="mcc-empty">
          <FileText />
          <h2>{own.length ? "해당 상태의 작업이 없습니다" : staff ? "고객이 보낸 발행 요청이 없습니다" : "첫 글을 발행해 보세요"}</h2>
          <p>{own.length ? "다른 상태를 선택해 보세요." : staff ? "고객 웹 › 작업 › 새 발행으로 요청하면 여기에 쌓입니다." : "원고와 사진을 보내면 발행 진행 상황을 여기에서 확인할 수 있습니다."}</p>
          {canWrite && onCreate && !own.length && <Button onClick={onCreate}>새 발행</Button>}
        </div>
      ) : (
        <div className="lwpub-list">
          {visible.map(({ r, status }) => {
            const unread = staff ? (r.unreadForStaff ?? 0) > 0 : (r.unreadForCustomer ?? 0) > 0;
            const scheduled = ["확인 중", "수정 요청", "발행 대기"].includes(status) ? futureAt(r.jobRunAt) : null;
            return (
              <article className="lwpub-card" key={r.id}>
                <div className="lwpub-card-main">
                  <div className="lwd-actions">
                    <Badge tone={publishTone(status)}>{status}</Badge>
                    {unread && (
                      <Badge tone="blue">
                        <MessageCircle size={12} /> 읽지 않은 답글
                      </Badge>
                    )}
                  </div>
                  <button className="meta-crew-text-button lwpub-title" onClick={() => onOpen(r.id)}>
                    {jobTitle(r.jobName)}
                  </button>
                  <small className="meta-crew-muted">
                    {r.id.slice(0, 8)} · 원고 v{r.revision ?? 1}
                  </small>
                  {status === "발행 중" && <PublishProgress current={stageIndexOf(status, r.stage)} compact />}
                  <p>
                    {scheduled
                      ? `예약 ${fmtDateTime(scheduled)}`
                      : status === "발행됨"
                        ? `게시 ${fmtDateTime(r.postedAt ?? r.finishedAt)}`
                        : status === "발행 실패"
                          ? staff
                            ? failureText(r.taskErrorKind ?? r.errorKind, r.errorMessage)
                            : (r.errorMessage ?? failureText(null))
                          : `원고 접수 ${fmtDateTime(r.createdAt)}`}
                  </p>
                </div>
                <Button variant="secondary" size="sm" onClick={() => onOpen(r.id)}>
                  상세 보기
                </Button>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------- 작성 (새 발행 · 고쳐 쓰기 · 다시 발행) ---------- */

export type ComposerSubmit = { manuscript: string; files: File[]; keepFileIds: string[]; scheduled: string; memo: string };

export function PublishComposer({
  customerId,
  onCancel,
  onSubmit,
  initialManuscript = "",
  existingFiles = [],
  initialSchedule,
  onDirtyChange,
  submitLabel = "발행 요청",
  approval = "staff",
  allowSchedule = true,
  busy = false,
  error,
}: {
  customerId: string;
  onCancel: () => void;
  onSubmit: (v: ComposerSubmit) => void;
  initialManuscript?: string;
  existingFiles?: { id: string; name: string | null }[];
  initialSchedule?: string;
  onDirtyChange?: (dirty: boolean) => void;
  submitLabel?: string;
  approval?: "staff" | "auto";
  allowSchedule?: boolean;
  busy?: boolean;
  error?: string | null;
}) {
  const formId = useId();
  const [manuscript, setManuscript] = useState(initialManuscript);
  const [files, setFiles] = useState<File[]>([]);
  const [when, setWhen] = useState(initialSchedule ? "예약" : "지금");
  const [schedule, setSchedule] = useState(initialSchedule ?? "");
  const [memo, setMemo] = useState("");
  const [discard, setDiscard] = useState(false);
  // 새 파일을 고르지 않으면 이전 버전의 첨부를 그대로 쓴다.
  const keep = files.length === 0 ? existingFiles.filter((f) => f.name) : [];
  const names = files.length ? files.map((f) => f.name) : keep.map((f) => f.name!);
  const preview = useManuscriptPreview(customerId, manuscript, names, true);
  const dirty = manuscript !== initialManuscript || files.length > 0 || !!memo || schedule !== (initialSchedule ?? "");
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  const blocked = !manuscript.trim() || !preview || preview.errors.length > 0 || busy;
  return (
    <form
      id={formId}
      className="mcc-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!blocked) onSubmit({ manuscript, files, keepFileIds: keep.map((f) => f.id), scheduled: when === "예약" ? schedule : "", memo });
      }}
    >
      <ManuscriptEditor value={manuscript} onChange={setManuscript} files={files} onFiles={setFiles} preview={preview} formId={formId} />
      {!files.length && keep.length > 0 && <p className="meta-crew-muted">이전 원고의 첨부 {keep.length}개를 그대로 씁니다: {keep.map((f) => f.name).join(" · ")}</p>}
      {preview && preview.settings.visibility !== "public" && (
        <p className="meta-crew-muted">{VISIBILITY_NAMES[preview.settings.visibility]} 공개 글은 검색에 노출되지 않아 SEO 순위를 추적하지 않습니다.</p>
      )}
      {allowSchedule && (
        <>
          <Select label="실행 시점" value={when} onChange={(e) => setWhen(e.target.value)}>
            <option>지금</option>
            <option>예약</option>
          </Select>
          {when === "예약" && <Field label="예약 시각 (한국 시간)" type="datetime-local" required value={schedule} onChange={(e) => setSchedule(e.target.value)} />}
        </>
      )}
      <label className="field">
        <span>운영팀 메모</span>
        <textarea className="input" rows={3} value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="확인할 내용을 남겨 주세요." maxLength={4000} />
      </label>
      <p className="meta-crew-muted">{approval === "auto" ? "형식 확인 후 발행 대기에 등록됩니다." : "운영팀이 원고를 확인한 뒤 발행합니다."} 예약은 한 번만 실행됩니다.</p>
      {error && (
        <p className="lwd-errors" role="alert">
          {error}
        </p>
      )}
      {discard ? (
        <div className="lwpub-policy" role="alert">
          <span>작성한 내용을 버릴까요?</span>
          <Button variant="secondary" onClick={() => setDiscard(false)}>
            계속 작성
          </Button>
          <Button variant="danger" onClick={onCancel}>
            버리고 닫기
          </Button>
        </div>
      ) : (
        <DialogActions secondary={{ label: "취소", onClick: () => (dirty ? setDiscard(true) : onCancel()) }} primary={{ label: busy ? "보내는 중" : submitLabel, type: "submit", disabled: blocked }} />
      )}
    </form>
  );
}

/** 첨부 업로드 + 등록 (새 발행·다시 발행). 메모는 등록 뒤 스레드 첫 답글로 남긴다. */
async function submitNew(customerId: string, v: ComposerSubmit): Promise<{ runId: string | null }> {
  const attachments = [...v.keepFileIds.map((fileId) => ({ fileId }))];
  for (const f of v.files) attachments.push({ fileId: (await api.upload<{ id: string }>(`/c/${customerId}/files`, f)).id });
  const body: Record<string, unknown> = { type: "naver_blog_post", params: { manuscript: v.manuscript }, ...(attachments.length ? { attachments } : {}) };
  if (v.scheduled) body.runAt = new Date(v.scheduled).toISOString();
  const r = await api.post<{ job: { id: string }; run: { id: string } | null }>(`/c/${customerId}/tasks`, body);
  if (v.memo.trim() && r.job?.id) await api.post(`/c/${customerId}/jobs/${r.job.id}/comments`, { body: v.memo.trim() });
  return { runId: r.run?.id ?? null };
}

export function NewPublishDialog({
  open,
  onClose,
  customerId,
  approval = "staff",
  initialManuscript,
  existingFiles,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  customerId: string;
  approval?: "staff" | "auto";
  initialManuscript?: string;
  existingFiles?: { id: string; name: string | null }[];
  onCreated?: (runId: string | null) => void;
}) {
  const [dirty, setDirty] = useState(false);
  const [discard, setDiscard] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = useAction((v: ComposerSubmit) => submitNew(customerId, v), [["c", customerId], ["admin", "runs"], ["admin", "overview"]]);
  useEffect(() => {
    if (open) setError(null);
  }, [open]);
  const close = () => (dirty ? setDiscard(true) : onClose());
  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && close()} title="새 발행" description="내 블로그에 올릴 원고와 사진을 준비해 주세요." size="wide">
        {open && (
          <PublishComposer
            customerId={customerId}
            onDirtyChange={setDirty}
            initialManuscript={initialManuscript}
            existingFiles={existingFiles}
            approval={approval}
            busy={send.isPending}
            error={error}
            onCancel={() => {
              setDirty(false);
              onClose();
            }}
            onSubmit={(v) => {
              setError(null);
              send
                .mutateAsync(v)
                .then((r) => {
                  setDirty(false);
                  onClose();
                  onCreated?.(r.runId);
                })
                .catch((e) => setError(errorMessage(e)));
            }}
          />
        )}
      </Dialog>
      <Dialog
        open={discard}
        onOpenChange={setDiscard}
        title="작성 중인 원고"
        footer={
          <DialogActions
            secondary={{ label: "계속 작성", onClick: () => setDiscard(false) }}
            primary={{
              label: "버리고 닫기",
              onClick: () => {
                setDiscard(false);
                setDirty(false);
                onClose();
              },
            }}
          />
        }
      >
        <p>저장하지 않은 원고를 버리고 닫을까요?</p>
      </Dialog>
    </>
  );
}

/* ---------- 상세 ---------- */

/** 원고 머리말·제목을 가볍게 읽는다 (화면 표시용. 등록 검사는 코어 파서). */
function readManuscript(md: string) {
  const head = /^\s*---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(md);
  const meta = new Map<string, string>();
  for (const line of head?.[1].split(/\r?\n/) ?? []) {
    const m = /^\s*([^:：]+)[:：]\s*(.*)$/.exec(line);
    if (m) meta.set(m[1]!.trim(), m[2]!.trim());
  }
  const body = head ? md.slice(head[0].length) : md;
  const title = /^#\s+(.+)$/m.exec(body)?.[1]?.trim() ?? "";
  const blocks = body
    .replace(/^#\s+.+$/m, "")
    .split(/\r?\n\s*\r?\n/)
    .map((b) => b.trim())
    .filter((b) => b && !b.startsWith("![") && !b.startsWith("<!--"));
  return { title, visibility: meta.get("공개") ?? meta.get("공개범위") ?? "전체", tags: meta.get("태그") ?? "", blocks };
}

/**
 * 발행 요청 상세 (시안 PublishDetail). 고객: 진행·결과·원고 버전·대화·고쳐 쓰기·취소·다시 시도·다시 발행.
 * 직원: 같은 상세에 운영팀 확인(승인·수정 요청·반려)과 연결된 실행 상세.
 */
export function PublishDetailDialog({
  customerId,
  runId,
  staff,
  onClose,
  onBack,
  onRun,
  onOpenRun,
}: {
  customerId: string;
  runId: string;
  staff: boolean;
  onClose: () => void;
  onBack?: () => void;
  onRun?: () => void;
  onOpenRun?: (runId: string) => void;
}) {
  const me = useMe();
  const run = useRun(customerId, runId);
  const r = run.data as RunDetail | undefined;
  const task = useTask(customerId, r?.taskId ?? null);
  const t = task.data as TaskDetail | undefined;
  const revisions = useJobRevisions(customerId, r?.jobId ?? null);
  const comments = useJobComments(customerId, r?.jobId ?? null);
  const status: PublishStatus = r ? publishStatusOf({ ...r, reviewStatus: t?.resultReview?.status ?? r.reviewStatus }, t?.status) : "확인 중";
  const canWrite = !staff && can(me, "PUBLISH_WRITE");
  const canApprove = staff && can(me, "TASK_CREATE");
  const canReply = staff ? can(me, "TASK_DETAIL") && can(me, "TASK_CREATE") : can(me, "PUBLISH_WRITE");
  const [mode, setMode] = useState<"" | "edit" | "republish" | "approve" | "changes" | "reject" | "cancel">("");
  const [version, setVersion] = useState<number | null>(null);
  const [reply, setReply] = useState("");
  const [reason, setReason] = useState("");
  const [keywords, setKeywords] = useState<string[]>([]);
  const [schedule, setSchedule] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exitPrompt, setExitPrompt] = useState(false);
  const base = `/c/${customerId}/runs/${runId}`;
  const invalidate = [keys.run(customerId, runId), ["c", customerId], ["admin", "runs"], ["admin", "overview"]];
  const action = useAction((v: { path: string; body?: unknown }) => api.post(`${base}/${v.path}`, v.body ?? {}), invalidate);
  const sendReply = useAction((text: string) => api.post(`/c/${customerId}/jobs/${r!.jobId}/comments`, { body: text }), [keys.comments(customerId, r?.jobId ?? ""), ["c", customerId]]);
  const resubmit = useAction(async (v: ComposerSubmit) => {
    const attachments = [...v.keepFileIds.map((fileId) => ({ fileId }))];
    for (const f of v.files) attachments.push({ fileId: (await api.upload<{ id: string }>(`/c/${customerId}/files`, f)).id });
    await api.post(`${base}/resubmit`, { type: "naver_blog_post", params: { manuscript: v.manuscript }, ...(attachments.length ? { attachments } : {}) });
    if (v.memo.trim() && r?.jobId) await api.post(`/c/${customerId}/jobs/${r.jobId}/comments`, { body: v.memo.trim() });
  }, [...invalidate, ["c", customerId, "job", r?.jobId ?? "", "revisions"]]);
  const republish = useAction((v: ComposerSubmit) => submitNew(customerId, v), [["c", customerId], ["admin", "runs"]]);

  const revs: JobRevision[] = revisions.data?.items ?? [];
  const latest = revs.at(-1);
  const shownRev = version ?? r?.revision ?? 1;
  const shown = revs.find((x) => x.revision === shownRev) ?? latest;
  const md = shown?.manuscript ?? r?.manuscript ?? "";
  const parsed = readManuscript(md);
  const awaiting = status === "확인 중" || status === "수정 요청";
  const failKind = t?.errorKind ?? r?.taskErrorKind ?? r?.errorKind ?? "";
  const retry = status === "발행 실패" && ENV_FAILURES.has(failKind);
  const cancellable = !!r && ["queued", "held"].includes(r.status);
  const url = (t?.result as { posted?: { url?: string | null } } | null | undefined)?.posted?.url ?? null;
  const postedAt = (t?.result as { posted?: { postedAt?: string } } | null | undefined)?.posted?.postedAt ?? r?.finishedAt ?? null;
  const scheduled = futureAt(r?.jobRunAt);
  const stageIdx = stageIndexOf(status, (t?.result as { stage?: string } | null | undefined)?.stage ?? r?.stage);
  const editing = mode === "edit" || mode === "republish";
  const exec = (p: Promise<unknown>, done: string) => {
    setError(null);
    p.then(() => {
      setMode("");
      setNote(done);
    }).catch((e) => setError(errorMessage(e)));
  };

  return (
    <Dialog
      open
      title={staff ? "고객 발행 요청 상세" : "작업 상세"}
      description={r ? `${r.id.slice(0, 8)} · ${r.customerName ?? ""}` : ""}
      size="detail"
      onOpenChange={(v) => {
        if (!v) {
          if (editing) setExitPrompt(true);
          else onClose();
        }
      }}
      footer={editing ? undefined : <DialogActions note={error ?? note ?? undefined} secondary={onBack ? { label: "목록으로", onClick: onBack } : undefined} primary={{ label: "닫기", onClick: onClose }} />}
    >
      {run.isLoading ? (
        <Loading />
      ) : run.error || !r ? (
        <PanelError error={errorMessage(run.error)} onRetry={() => void run.refetch()} />
      ) : editing ? (
        <PublishComposer
          key={mode}
          customerId={customerId}
          initialManuscript={latest?.manuscript ?? r.manuscript ?? ""}
          existingFiles={latest?.files ?? []}
          allowSchedule={mode === "republish"}
          submitLabel={mode === "edit" ? "수정 원고 다시 제출" : "이 원고로 발행 요청"}
          busy={resubmit.isPending || republish.isPending}
          error={error}
          onCancel={() => setMode("")}
          onSubmit={(v) => {
            setError(null);
            if (mode === "edit") {
              resubmit
                .mutateAsync(v)
                .then(() => {
                  setMode("");
                  setVersion(null);
                  setNote("수정 원고를 다시 제출했습니다.");
                })
                .catch((e) => setError(errorMessage(e)));
            } else {
              republish
                .mutateAsync(v)
                .then((x) => {
                  setMode("");
                  setNote("새 발행 요청을 보냈습니다.");
                  if (x.runId && onOpenRun) onOpenRun(x.runId);
                })
                .catch((e) => setError(errorMessage(e)));
            }
          }}
        />
      ) : (
        <div className="lwd-stack">
          <div className="lwpub-detail-heading">
            <div>
              <Badge tone={publishTone(status)}>{status}</Badge>
              <h2>{jobTitle(r.jobName)}</h2>
            </div>
            <small>원고 v{r.revision ?? 1}</small>
          </div>
          <ol className="lwpub-timeline" aria-label="발행 요청 타임라인">
            <li>접수</li>
            <li>{awaiting ? status : "확인 완료"}</li>
            <li>{scheduled ? `예약 ${fmtDateTime(scheduled)}` : "발행 대기"}</li>
            <li>{["발행 중", "발행됨", "발행 실패", "삭제됨"].includes(status) ? status : "발행 예정"}</li>
          </ol>
          {(status === "발행 중" || status === "발행됨") && (
            <Panel title="발행 진행">
              <div className="lwd-panel-body">
                <PublishProgress current={stageIdx} />
              </div>
            </Panel>
          )}
          {status === "발행 실패" && (
            <div className="lwpub-policy" role="status">
              {staff ? failureText(failKind, r.errorMessage) : (r.errorMessage ?? failureText(null))}
            </div>
          )}
          {(status === "수정 요청" || status === "반려됨") && r.errorMessage && (
            <div className="lwpub-policy" role="status">
              {status === "수정 요청" ? "수정 요청" : "반려"} 사유: {r.errorMessage}
            </div>
          )}
          {status === "발행됨" && (
            <Panel title="발행 결과">
              <InfoRow title="게시 완료" description={`${fmtDateTime(postedAt)} · 원고 v${r.revision ?? 1}으로 발행했습니다.`} />
              <div className="lwd-panel-body">
                {url ? (
                  <a href={url} target="_blank" rel="noreferrer">
                    게시 글 보기 ↗
                  </a>
                ) : (
                  <p>게시 주소를 확인하지 못했습니다. 블로그의 내 글 목록에서 확인할 수 있습니다.</p>
                )}
              </div>
            </Panel>
          )}
          {canWrite && (
            <div className="lwd-actions">
              {awaiting && <Button onClick={() => setMode("edit")}>고쳐 쓰기</Button>}
              {cancellable && (
                <Button variant="secondary" onClick={() => setMode("cancel")}>
                  발행 취소
                </Button>
              )}
              {retry && (
                <Button onClick={() => exec(action.mutateAsync({ path: "retry" }), "다시 시도를 요청했습니다.")} disabled={action.isPending}>
                  다시 시도
                </Button>
              )}
              {(status === "삭제됨" || status === "발행 실패" || status === "취소됨" || status === "반려됨") && (
                <Button variant="secondary" onClick={() => setMode("republish")}>
                  이 원고로 다시 발행
                </Button>
              )}
            </div>
          )}
          {canApprove && awaiting && (
            <Panel title="운영팀 확인">
              <div className="lwd-panel-body lwd-stack">
                <p>원고 v{r.revision ?? 1}을 확인하고 발행 일정을 지정해 주세요.{futureAt(r.jobRunAt) ? ` 고객 희망 일시 ${fmtDateTime(r.jobRunAt)}.` : ""}</p>
                <div className="lwd-actions">
                  <Button onClick={() => setMode("approve")}>승인</Button>
                  <Button variant="secondary" onClick={() => setMode("changes")}>
                    수정 요청
                  </Button>
                  <Button variant="secondary" onClick={() => setMode("reject")}>
                    반려
                  </Button>
                </div>
              </div>
            </Panel>
          )}
          {staff && !awaiting && (
            <InfoRow
              title="결과 검토함 상태"
              description={status === "삭제됨" ? "삭제 완료" : t?.resultReview ? `검토 ${t.resultReview.status === "pending" ? "대기" : t.resultReview.status}` : "발행 완료 후 생성됩니다"}
            />
          )}
          {staff && !awaiting && onRun && (
            <Button variant="secondary" onClick={onRun}>
              연결된 실행 상세
            </Button>
          )}
          {mode === "approve" && (
            <Panel title="발행 승인">
              <form
                className="lwd-panel-body mcc-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  const kw = parsed.visibility === "전체" ? keywords : [];
                  exec(action.mutateAsync({ path: "approve", body: { ...(kw.length ? { keywords: kw } : {}), ...(schedule ? { runAt: new Date(schedule).toISOString() } : {}) } }), "승인했습니다.");
                }}
              >
                {parsed.visibility === "전체" ? (
                  <KeywordInput value={keywords} onChange={setKeywords} />
                ) : (
                  <p>비공개·이웃 공개 원고는 SEO 키워드를 저장하지 않습니다.</p>
                )}
                <Field label="예약 시각 (비워두면 고객 희망 일시 또는 지금)" type="datetime-local" value={schedule} onChange={(e) => setSchedule(e.target.value)} />
                <DialogActions secondary={{ label: "취소", onClick: () => setMode("") }} primary={{ label: "승인하고 발행 대기", type: "submit", disabled: action.isPending }} />
              </form>
            </Panel>
          )}
          {(mode === "changes" || mode === "reject" || mode === "cancel") && (
            <Panel title={mode === "cancel" ? "발행 취소 확인" : mode === "changes" ? "수정 요청 사유" : "반려 사유"}>
              <form
                className="lwd-panel-body mcc-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (mode === "cancel") exec(action.mutateAsync({ path: "cancel" }), "발행 요청을 취소했습니다.");
                  else
                    exec(action.mutateAsync({ path: mode === "changes" ? "request-changes" : "reject", body: { reason: reason.trim() } }), mode === "changes" ? "수정을 요청했습니다." : "반려했습니다.");
                  setReason("");
                }}
              >
                {mode === "cancel" ? <p>발행 대기 중인 요청을 취소합니다.</p> : <Field label="고객에게 보낼 사유" value={reason} onChange={(e) => setReason(e.target.value)} required maxLength={2000} />}
                <DialogActions secondary={{ label: "돌아가기", onClick: () => setMode("") }} primary={{ label: mode === "cancel" ? "취소 확정" : "사유 보내기", type: "submit", disabled: action.isPending }} />
              </form>
            </Panel>
          )}
          <Panel title="원고와 첨부">
            <div className="lwd-panel-body lwd-stack">
              {revs.length > 1 && (
                <Select label="원고 버전" value={shownRev} onChange={(e) => setVersion(Number(e.target.value))}>
                  {revs.map((x) => (
                    <option value={x.revision} key={x.revision}>
                      v{x.revision}
                      {x.revision === (r.revision ?? 1) ? " · 현재 원고" : " · 이전 원고"}
                    </option>
                  ))}
                </Select>
              )}
              <article className="lwpub-manuscript">
                <h3>{parsed.title || jobTitle(r.jobName)}</h3>
                <p className="meta-crew-muted">
                  {parsed.visibility} 공개 · 태그 {parsed.tags || "없음"}
                </p>
                {parsed.blocks.map((block, i) => (
                  <p key={i}>{block.replace(/^#{2,6} /, "").replace(/^> ?/, "")}</p>
                ))}
              </article>
              <div className="lwpub-attachments">
                {(shown?.files ?? []).map((f) => (
                  <figure key={f.id}>
                    <FileText size={32} />
                    <figcaption>{f.name ?? f.id.slice(0, 8)}</figcaption>
                  </figure>
                ))}
                {!shown?.files?.length && !!r.attachmentCount && <p className="meta-crew-muted">첨부 {r.attachmentCount}개</p>}
              </div>
            </div>
          </Panel>
          <Panel title="운영팀과 대화" extra={<Badge>원고별 기록</Badge>}>
            <div className="lwd-panel-body lwd-stack">
              {comments.isLoading ? (
                <Loading />
              ) : comments.error ? (
                <PanelError error={errorMessage(comments.error)} onRetry={() => void comments.refetch()} />
              ) : (
                <ol className="lwpub-thread">
                  {(comments.data?.items ?? []).map((c) => (
                    <li key={c.id} className={`is-${c.authorKind === "system" ? "system" : c.authorKind === "customer" ? "customer" : "staff"}`}>
                      <div>
                        <strong>{c.authorKind === "system" ? "시스템" : c.authorKind === "staff" ? "운영팀" : (c.authorName ?? "고객")}</strong>
                        <small>
                          v{c.revision} · {fmtDateTime(c.createdAt)}
                        </small>
                      </div>
                      <p>{c.body}</p>
                    </li>
                  ))}
                </ol>
              )}
              {canReply && r.jobId && (
                <form
                  className="mcc-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const text = reply.trim();
                    if (!text) return;
                    sendReply
                      .mutateAsync(text)
                      .then(() => {
                        setReply("");
                        setNote("답글을 보냈습니다.");
                      })
                      .catch((er) => setError(errorMessage(er)));
                  }}
                >
                  <label className="field">
                    <span>답글 · 원고 v{r.revision ?? 1}</span>
                    <textarea className="input" rows={3} value={reply} onChange={(e) => setReply(e.target.value)} required maxLength={4000} />
                  </label>
                  <div className="lwd-actions">
                    <Button type="submit" disabled={!reply.trim() || sendReply.isPending}>
                      답글 보내기
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </Panel>
        </div>
      )}
      <Dialog
        open={exitPrompt}
        onOpenChange={setExitPrompt}
        title="작성 중인 수정 원고"
        footer={<DialogActions secondary={{ label: "계속 작성", onClick: () => setExitPrompt(false) }} primary={{ label: "버리고 닫기", onClick: onClose }} />}
      >
        <p>저장하지 않은 변경을 버리고 닫을까요?</p>
      </Dialog>
    </Dialog>
  );
}

/* ---------- 고객 헤더 알림 ---------- */

const SEEN_KEY = "meta-crew.publishSeen";
function loadSeen(): Record<string, true> | null {
  try {
    const v = localStorage.getItem(SEEN_KEY);
    return v ? (JSON.parse(v) as Record<string, true>) : null;
  } catch {
    return null;
  }
}
/** 이미 본 상태 전환 (runId:상태). 수정 요청·반려·발행됨은 한 번만 알린다. 새 답글은 서버 읽음 수(unreadForCustomer)로 판단. */
const useSeen = create<{ seen: Record<string, true> | null; mark: (keys: string[]) => void }>((set, get) => ({
  seen: loadSeen(),
  mark: (keys) => {
    const next = { ...(get().seen ?? {}) };
    for (const k of keys) next[k] = true;
    try {
      localStorage.setItem(SEEN_KEY, JSON.stringify(next));
    } catch {
      /* 저장 못 해도 알림은 동작 */
    }
    set({ seen: next });
  },
}));
const NOTIFY: PublishStatus[] = ["수정 요청", "반려됨", "발행됨"];

export function CustomerPublishNotifications({ customerId }: { customerId: string }) {
  const q = useRuns(customerId, { origin: "customer", limit: 50 });
  const { seen, mark } = useSeen();
  const [open, setOpen] = useState(false);
  const rows = q.data?.items ?? [];
  // 처음 쓰는 브라우저: 지금 상태는 이미 본 것으로 둔다 (과거 전환을 한꺼번에 알리지 않음).
  useEffect(() => {
    if (seen === null && q.data) mark(rows.map((r) => `${r.id}:${publishStatusOf(r)}`));
  }, [seen, q.data, rows, mark]);
  const notices = rows
    .map((r) => {
      const status = publishStatusOf(r);
      const reply = (r.unreadForCustomer ?? 0) > 0;
      const transition = NOTIFY.includes(status) && seen !== null && !seen[`${r.id}:${status}`];
      return reply || transition ? { r, status, label: reply && !transition ? "새 답글" : status } : null;
    })
    .filter((x): x is { r: RunRow; status: PublishStatus; label: string } => !!x);
  return (
    <>
      <IconButton label={`발행 알림${notices.length ? ` · 읽지 않음 ${notices.length}건` : ""}`} className="lwi-notification-trigger" onClick={() => setOpen(true)}>
        <Bell size={17} />
        {notices.length > 0 && <span className="lwpub-unread-count">{notices.length}</span>}
      </IconButton>
      <Dialog open={open} onOpenChange={setOpen} title="발행 알림" footer={<DialogActions primary={{ label: "닫기", onClick: () => setOpen(false) }} />}>
        <div className="lwi-notice-list">
          {notices.length ? (
            notices.map(({ r, status, label }) => (
              <section key={r.id}>
                <MessageCircle size={18} />
                <div>
                  <strong>{label}</strong>
                  <p>{jobTitle(r.jobName)}</p>
                  {(status === "수정 요청" || status === "반려됨") && r.errorMessage && <p>{r.errorMessage}</p>}
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setOpen(false);
                      mark([`${r.id}:${status}`]);
                      usePublishFocus.getState().open(r.id);
                    }}
                  >
                    작업 상세 보기
                  </Button>
                </div>
              </section>
            ))
          ) : (
            <div className="mcc-empty">읽지 않은 알림이 없습니다.</div>
          )}
        </div>
      </Dialog>
    </>
  );
}
